import { IStageContentInfo } from "../../Courses/constants/emptyCourseScenario";
import { FileTypes } from "../../Courses/enums/FileTypes.enum";
import { ScenarioMessageTypes } from "../enums/ScenarioMessageTypes.enum";
import { IConnectedUser } from "../interfaces/IConnectedUser.interface";
import { ScenarioMessageInfo } from "../interfaces/ScenarioMessageInfo.interface";

/**
 * The protocol of a running scenario as the paper form "Anlage 3 – Vordruck
 * Ereignisdokumentation" (BCM360-376). The API adds optional fields to an entry's `data`:
 * `recordedAt` (the "Uhrzeit"; `timestamp` when missing), `recordedInPhaseId`, `assigneeId`,
 * `doneBy`/`doneAt` and `editedBy`/`editedAt`. Every id is a run user's id.
 */
export const PROTOCOL_TEXT_LIMIT = 1000;

/** Texts the server writes itself; they are worded here and nobody edits them. */
export const KEY_MESSAGES = ["requested-spoiler", "user-joined", "removed-user", "invited-users"];

export type ProtocolPhase = { id: number | null; phaseName?: string };

export type ProtocolRow =
	| { kind: "phase"; key: string; phaseId: number; at: number }
	| { kind: "time"; key: string; phaseId?: number; at: number; message: ScenarioMessageInfo }
	| { kind: "end"; key: string; phaseId?: number; at: number }
	| {
			kind: "entry";
			key: string;
			/** The entry's own phase, or the one running at its time for entries written before. */
			phaseId?: number;
			at: number;
			message: ScenarioMessageInfo;
	  };

export type EntryRow = Extract<ProtocolRow, { kind: "entry" }>;

export type StatusFilter = "all" | "open" | "done";

/** The time in the "Uhrzeit" column. */
export const shownAt = (message: ScenarioMessageInfo): number =>
	message.type === ScenarioMessageTypes.MESSAGE && Number(message.data?.recordedAt) > 0
		? Number(message.data.recordedAt)
		: message.timestamp;

export const isOpen = (message: ScenarioMessageInfo) =>
	!!message.data?.assigneeId && !message.data?.doneAt;

export const isDone = (message: ScenarioMessageInfo) =>
	!!message.data?.assigneeId && !!message.data?.doneAt;

/**
 * Content a phase starts with: shown when the phase begins, not later (time-delayed content
 * has a start time) and not a hint the team asked for.
 */
const startsPhase = (content: IStageContentInfo) =>
	typeof content?.stageNumber === "number" &&
	content.startTimeInSeconds === undefined &&
	content.endTimeInSeconds === undefined &&
	content.contentType !== FileTypes.Spoiler;

const RANK: Record<ProtocolRow["kind"], number> = { phase: 0, entry: 1, time: 1, end: 2 };

/**
 * The form's rows in the order of their "Uhrzeit". A phase gets a divider row where its own
 * content starts; a phase without content of its own has none, and its entries are known by
 * their phase label. Time warnings and the end of the run are rows of their own; a decision
 * is shown once it is decided, as before.
 */
export function buildProtocolRows(
	messages: ScenarioMessageInfo[],
	stageContent: IStageContentInfo[]
): ProtocolRow[] {
	const rows: ProtocolRow[] = [];

	stageContent.filter(startsPhase).forEach((content, index) =>
		rows.push({
			kind: "phase",
			key: `phase-${content.id ?? index}`,
			phaseId: content.stageNumber as number,
			at: Number(content.timeStamp) || 0,
		})
	);

	(messages ?? []).forEach((message) => {
		if (message.type === ScenarioMessageTypes.MESSAGE) {
			rows.push({
				kind: "entry",
				key: message.id,
				phaseId: message.data?.recordedInPhaseId,
				at: shownAt(message),
				message,
			});
		} else if (message.type === ScenarioMessageTypes.DECISION && message.data?.finalDecision) {
			rows.push({
				kind: "entry",
				key: message.id,
				phaseId: message.data?.recordedInPhaseId,
				at: message.timestamp,
				message,
			});
		} else if (message.type === ScenarioMessageTypes.SERVICE) {
			rows.push({ kind: "time", key: message.id, at: message.timestamp, message });
		} else if (message.type === ScenarioMessageTypes.END) {
			rows.push({ kind: "end", key: message.id, at: message.timestamp });
		}
	});

	// An entry stays inside the visit of the phase it was written in, whatever its "Uhrzeit":
	// not before that visit's divider, and not after the next phase's. A decision is stamped
	// when it is decided, a moment after the next phase has already begun.
	const starts = rows.filter((row) => row.kind === "phase").map((row) => row.at);
	const visits = rows.filter(
		(row): row is Extract<ProtocolRow, { kind: "phase" }> => row.kind === "phase"
	);
	const placeOf = (row: ProtocolRow) => {
		if (row.kind !== "entry" || typeof row.phaseId !== "number") {
			return row.at;
		}

		const writtenAt = Math.max(row.at, row.message.timestamp);
		const visitStart = visits
			.filter((phase) => phase.phaseId === row.phaseId && phase.at <= writtenAt)
			.reduce((latest, phase) => Math.max(latest, phase.at), -Infinity);

		if (visitStart === -Infinity) {
			return row.at;
		}

		const nextStart = Math.min(...starts.filter((at) => at > visitStart), Infinity);

		return Math.min(Math.max(row.at, visitStart), nextStart - 1);
	};

	const sorted = rows
		.map((row, index) => ({ row, index, place: placeOf(row) }))
		.sort((a, b) => a.place - b.place || RANK[a.row.kind] - RANK[b.row.kind] || a.index - b.index)
		.map(({ row }) => row);

	// Rows without a phase of their own belong to the phase running at their time.
	let running: number | undefined;

	return sorted.map((row): ProtocolRow => {
		if (typeof row.phaseId === "number") {
			running = row.phaseId;

			return row;
		}

		return row.kind === "phase" ? row : { ...row, phaseId: running };
	});
}

/** The phases the protocol has rows for, in the order they first appear. */
export function protocolPhases(rows: ProtocolRow[]): number[] {
	return [
		...new Set(rows.map((row) => row.phaseId).filter((id): id is number => id !== undefined)),
	];
}

export function filterProtocolRows(rows: ProtocolRow[], status: StatusFilter, phaseId?: number) {
	return rows.filter((row) => {
		if (phaseId !== undefined && row.phaseId !== phaseId) {
			return false;
		}

		if (status === "all") {
			return true;
		}

		if (row.kind !== "entry") {
			return false;
		}

		return status === "open" ? isOpen(row.message) : isDone(row.message);
	});
}

type Named = { firstName?: string | null; lastName?: string | null } | undefined | null;

/** A person's name; never their placeholder e-mail (BCM360-351). */
export const nameOf = (user: Named) =>
	[user?.firstName, user?.lastName]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(" ");

/** "J. Wagner", as the "erledigt" column has room for. */
export const shortNameOf = (user: Named) => {
	const first = user?.firstName?.trim();
	const last = user?.lastName?.trim();

	return first && last ? `${first[0]}. ${last}` : nameOf(user);
};

/** The people an entry can be assigned to: everyone in the run with a name. */
export const assignableUsers = (users: IConnectedUser[]) =>
	users.filter((user) => !user.isRemoved && !!nameOf(user));

/** "09:31", in the browser's time, as the people in the run see it. */
export const clockOf = (timestamp: number) => {
	const date = new Date(timestamp);

	return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

/**
 * A typed "HH:MM" as a timestamp today, or null when it is not a time. A time more than twelve
 * hours ahead is yesterday's: an entry written after midnight about the evening before.
 */
export function timestampOfClock(value: string, now = Date.now()): number | null {
	const match = /^\s*(\d{1,2})[:.](\d{2})\s*$/.exec(value);

	if (!match) {
		return null;
	}

	const [hours, minutes] = [Number(match[1]), Number(match[2])];

	if (hours > 23 || minutes > 59) {
		return null;
	}

	const date = new Date(now);

	date.setHours(hours, minutes, 0, 0);

	const at = date.getTime();

	return at - now > 12 * 60 * 60 * 1000 ? at - 24 * 60 * 60 * 1000 : at;
}

/** A typed time that keeps the minute it was written in, so an untouched field changes nothing. */
export function recordedAtOf(typed: string, previous: number, now = Date.now()) {
	if (typed === clockOf(previous)) {
		return previous;
	}

	return timestampOfClock(typed, now);
}
